import { ReactNode } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Box,
    Typography,
    Stack,
    IconButton,
    useMediaQuery,
    useTheme,
    CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { styleHelpers, themeConstants } from '@/theme';

interface BaseModalProps {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    actions?: ReactNode;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    fullWidth?: boolean;
    fullScreen?: boolean;

    title?: string;
    centeredHeader?: {
        content: ReactNode;
    };
    styledHeader?: {
        icon?: ReactNode;
        title: string;
        subtitle?: string[];
        gradient?: string;
    };

    minHeight?: string | object;
    loading?: boolean;
}

const BaseModal = ({
    open,
    onClose,
    children,
    actions,
    maxWidth = 'md',
    fullWidth = true,
    fullScreen: fullScreenProp,
    title,
    styledHeader,
    centeredHeader,
    minHeight,
    loading = false,
}: BaseModalProps) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const fullScreen = fullScreenProp !== undefined ? fullScreenProp : isSmallScreen;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            fullScreen={fullScreen}
            slotProps={{
                paper: {
                    sx: {
                        ...(minHeight ? { minHeight } : null),
                        maxHeight: { xs: '100dvh', sm: 'calc(100dvh - 64px)' },
                        display: 'flex',
                        flexDirection: 'column',
                        // On iOS the notch/status bar can overlap the header when the
                        // dialog is fullScreen — keep the header clear of the safe area.
                        ...(fullScreen ? { pt: 'env(safe-area-inset-top)' } : null),
                    },
                },
            }}
        >
            {title && !styledHeader && !centeredHeader && (
                <Stack direction="row" sx={{ pr: 2, alignItems: 'center' }}>
                    <DialogTitle>{title}</DialogTitle>
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    />
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            )}
            {styledHeader && (
                <Box
                    sx={{
                        ...styleHelpers.dialogHeader(
                            styledHeader.gradient || themeConstants.gradients.primary
                        ),
                        p: { xs: 2, sm: 3 },
                    }}
                >
                    <Stack direction="row" sx={{ alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                        {styledHeader.icon && (
                            <Box
                                sx={{
                                    ...styleHelpers.iconBox(),
                                    width: { xs: 40, sm: 56 },
                                    height: { xs: 40, sm: 56 },
                                }}
                            >
                                {styledHeader.icon}
                            </Box>
                        )}
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '1.125rem', sm: '1.5rem' },
                                }}
                            >
                                {styledHeader.title}
                            </Typography>
                            {styledHeader.subtitle &&
                                styledHeader.subtitle.map((subtitle) => (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'rgba(255,255,255,0.8)',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        }}
                                    >
                                        {subtitle}
                                    </Typography>
                                ))}
                        </Box>
                        <IconButton
                            onClick={onClose}
                            size={isSmallScreen ? 'small' : 'medium'}
                            sx={{
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Box>
            )}
            {centeredHeader && (
                <Box
                    sx={{
                        ...styleHelpers.dialogHeader(),
                        textAlign: 'center',
                        p: 4,
                        position: 'relative',
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {centeredHeader.content}
                </Box>
            )}
            <DialogContent
                sx={{
                    ...(!title && !styledHeader && !centeredHeader ? { p: 0 } : null),
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {loading ? (
                    <Stack
                        direction="row"
                        sx={{ p: 3, alignItems: 'center', justifyContent: 'center', py: 4 }}
                    >
                        <CircularProgress />
                    </Stack>
                ) : (
                    children
                )}
            </DialogContent>
            {actions && (
                <DialogActions
                    sx={{
                        flexShrink: 0,
                        // Keep the footer buttons above the iOS home indicator / bottom
                        // browser chrome so they are never clipped on iPhone.
                        pb: 'max(env(safe-area-inset-bottom), 8px)',
                    }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            gap: 2,
                            flexWrap: 'wrap',
                            width: '100%',
                            justifyContent: 'right',
                        }}
                    >
                        {actions}
                    </Stack>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default BaseModal;
